import { AxiosInstance } from 'axios';

import { cosmosApi } from '../cosmosAPi/index';

export type AuthOutcome =
  | { type: 'ok'; id: number }
  | { type: 'pmais' }
  | { type: 'error' };

type ValidateAuthOptions = {
  api?: AxiosInstance;
  maxAttempts?: number;
  delayMs?: number;
};

const statusSuccess = (status: number) => status >= 200 && status < 300;
const invalidOrExpiredToken = (status: number) => status === 401 || status === 403;
const statusRetryableError = (status: number) =>
  status === 404 || (status >= 500 && status < 600);

const requestDelay = (ms: number) => new Promise(res => setTimeout(res, ms));

export const validateAuthToken = async (
  token: string,
  options: ValidateAuthOptions = {},
): Promise<AuthOutcome> => {
  const { api = cosmosApi(), maxAttempts = 5, delayMs = 1000 } = options;

  const normalizedToken = token.trim();

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await api.get('/v1/logged-user', {
        headers: {
          Authorization: `Bearer ${normalizedToken}`,
        },
        validateStatus: () => true,
      });

      const status = response.status;

      if (statusSuccess(status)) return { type: 'ok', id: response.data.id };
      if (invalidOrExpiredToken(status)) return { type: 'pmais' };

      if (statusRetryableError(status)) {
        if (attempt < maxAttempts) {
          await requestDelay(delayMs);
          continue;
        }
        return { type: 'error' };
      }

      return { type: 'error' };
    } catch {
      if (attempt < maxAttempts) {
        await requestDelay(delayMs);
        continue;
      }
      return { type: 'error' };
    }
  }

  return { type: 'error' };
};
